import type {
	AdvancedFilterLayoutMode,
	SplitRouteMemoryEntry,
} from '../settings.model';
import {
	persistAdvancedFilterCollapse,
	persistAdvancedFilterLayouts,
	persistSplitPanelWidths,
	persistSplitRouteMemory,
} from '../settings.utils';

/**
 * Layout preference actions.
 *
 * Handles split panel widths, advanced filter layout/collapse state and
 * split-view route memory, all persisted locally and synced via the UI
 * preference upsert queue.
 */
export const layoutPreferencesActions = {
	/**
	 * Returns the stored split panel width for a key.
	 */
	getSplitPanelWidth(this: any, key: string): number | null {
		if (!key) {
			return null;
		}

		const width = this.splitPanelWidths[key];
		if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
			return null;
		}

		return width;
	},

	/**
	 * Stores split panel width for a key.
	 */
	setSplitPanelWidth(this: any, key: string, width: number): void {
		if (
			!key ||
			typeof width !== 'number' ||
			!Number.isFinite(width) ||
			width <= 0
		) {
			return;
		}

		if (this.splitPanelWidths[key] === width) {
			return;
		}

		this.splitPanelWidths = {
			...this.splitPanelWidths,
			[key]: width,
		};
		persistSplitPanelWidths(this.splitPanelWidths);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Removes stored split panel width for a key.
	 */
	clearSplitPanelWidth(this: any, key: string): void {
		if (!key || !this.splitPanelWidths[key]) {
			return;
		}

		const next = { ...this.splitPanelWidths };
		delete next[key];
		this.splitPanelWidths = next;
		persistSplitPanelWidths(this.splitPanelWidths);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Returns advanced filter layout mode for a key.
	 */
	getAdvancedFilterLayoutMode(
		this: any,
		key: string
	): AdvancedFilterLayoutMode | null {
		if (!key) {
			return null;
		}

		const mode = this.advancedFilterLayouts[key];
		if (mode !== 'sidebar' && mode !== 'collapse') {
			return null;
		}

		return mode;
	},

	/**
	 * Stores advanced filter layout mode for a key.
	 */
	setAdvancedFilterLayoutMode(
		this: any,
		key: string,
		mode: AdvancedFilterLayoutMode
	): void {
		if (!key || (mode !== 'sidebar' && mode !== 'collapse')) {
			return;
		}

		if (this.advancedFilterLayouts[key] === mode) {
			return;
		}

		this.advancedFilterLayouts = {
			...this.advancedFilterLayouts,
			[key]: mode,
		};
		persistAdvancedFilterLayouts(this.advancedFilterLayouts);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Removes advanced filter layout mode for a key.
	 */
	clearAdvancedFilterLayoutMode(this: any, key: string): void {
		if (!key || !this.advancedFilterLayouts[key]) {
			return;
		}

		const next = { ...this.advancedFilterLayouts };
		delete next[key];
		this.advancedFilterLayouts = next;
		persistAdvancedFilterLayouts(this.advancedFilterLayouts);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Returns advanced filter collapse open state for a key.
	 */
	getAdvancedFilterCollapseOpen(this: any, key: string): boolean | null {
		if (!key || typeof this.advancedFilterCollapse[key] !== 'boolean') {
			return null;
		}

		return this.advancedFilterCollapse[key];
	},

	/**
	 * Stores advanced filter collapse open state for a key.
	 */
	setAdvancedFilterCollapseOpen(this: any, key: string, isOpen: boolean): void {
		if (!key) {
			return;
		}

		if (this.advancedFilterCollapse[key] === isOpen) {
			return;
		}

		this.advancedFilterCollapse = {
			...this.advancedFilterCollapse,
			[key]: Boolean(isOpen),
		};
		persistAdvancedFilterCollapse(this.advancedFilterCollapse);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Removes advanced filter collapse state for a key.
	 */
	clearAdvancedFilterCollapseOpen(this: any, key: string): void {
		if (!key || typeof this.advancedFilterCollapse[key] !== 'boolean') {
			return;
		}

		const next = { ...this.advancedFilterCollapse };
		delete next[key];
		this.advancedFilterCollapse = next;
		persistAdvancedFilterCollapse(this.advancedFilterCollapse);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Returns split route memory entry for a key.
	 */
	getSplitRouteMemory(this: any, key: string): SplitRouteMemoryEntry | null {
		if (!key) {
			return null;
		}

		const entry = this.splitRouteMemory[key];
		if (!entry?.name) {
			return null;
		}

		return entry;
	},

	/**
	 * Stores split route memory entry for a key.
	 */
	setSplitRouteMemory(
		this: any,
		key: string,
		entry: SplitRouteMemoryEntry
	): void {
		if (!key || !entry?.name || !entry.name.trim()) {
			return;
		}

		const normalizedEntry: SplitRouteMemoryEntry = {
			name: entry.name,
			params:
				entry.params && typeof entry.params === 'object' ? entry.params : {},
			query: entry.query && typeof entry.query === 'object' ? entry.query : {},
		};

		const currentEntry = this.splitRouteMemory[key];
		if (
			currentEntry &&
			currentEntry.name === normalizedEntry.name &&
			JSON.stringify(currentEntry.params || {}) ===
				JSON.stringify(normalizedEntry.params || {}) &&
			JSON.stringify(currentEntry.query || {}) ===
				JSON.stringify(normalizedEntry.query || {})
		) {
			return;
		}

		this.splitRouteMemory = {
			...this.splitRouteMemory,
			[key]: normalizedEntry,
		};
		persistSplitRouteMemory(this.splitRouteMemory);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Removes split route memory entry for a key.
	 */
	clearSplitRouteMemory(this: any, key: string): void {
		if (!key || !this.splitRouteMemory[key]) {
			return;
		}

		const next = { ...this.splitRouteMemory };
		delete next[key];
		this.splitRouteMemory = next;
		persistSplitRouteMemory(this.splitRouteMemory);
		this.queueUIPreferencesUpsert();
	},
};
